<template lang="pug">
.v-multiselect-wrapper
  label.v-multiselect__label(:class="{ 'v-multiselect__label--invalid': invalid }") {{ label }}
  .v-multiselect
    v-dropdown
      template(slot-scope="props")
        .v-multiselect__field(:class="{ 'v-multiselect__field--selected': value && value.length, 'v-multiselect__field--toggled': props.toggled }")
          input.v-multiselect__field-input(
            v-focus
            v-if="props.toggled"
            v-model="search"
            type="text"
            :placeholder="searchPlaceholder"
            @click.stop)
          .v-multiselect__placeholder(
            v-else
            :class="{ 'v-multiselect__placeholder--empty': !value || !value.length }") {{ value && value.length ? selectedLabel : selectPlaceholder }}
          .v-multiselect__bar
          .v-multiselect__chevron
            v-chevron(:value="props.toggled")
      template(slot="content")
        v-card(
          elevated
          overflow-hidden)
          v-scrollable(
            @scroll-complete="onScrollComplete")
            ul.v-multiselect__options
              template(v-if="filteredOptions.length")
                v-multiselect-item(
                  v-for="(item, index) in filteredOptions"
                  :key="index"
                  :item="item"
                  :item-name="itemName"
                  :item-subname="itemSubname"
                  :item-value="itemValue"
                  :selected="value"
                  :comparator="comparator"
                  @select="onSelect"
                  @unselect="onUnselect")
              li.v-multiselect__options-info(v-else) Немає елементів
  .v-multiselect__hint-wrapper
    .v-multiselect__hint(v-if="hint") {{ hint }}
</template>

<script>
import VCard from '../v-card/VCard'
import VChevron from '../v-chevron/VChevron'
import VDropdown from '../v-dropdown/VDropdown'
import VScrollable from '../v-scrollable/VScrollable'
import VMultiselectItem from './VMultiselectItem'

export default {
  name: 'VMultiselct',

  components: {
    VCard,
    VChevron,
    VDropdown,
    VScrollable,
    VMultiselectItem
  },

  props: {
    label: String,
    value: Array,
    options: Array,
    comparator: Function,
    hint: {
      type: String,
      default: null
    },
    invalid: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      default: null
    },
    selectPlaceholder: {
      type: String,
      default: function () {
        return this.$t('base.select')
      }
    },
    itemName: {
      type: String,
      default: 'name'
    },
    itemSubname: {
      type: String,
      default: null
    },
    itemValue: {
      type: String,
      default: null
    },
    usePrimitiveValue: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    search: null
  }),

  methods: {
    onSelect (item) {
      this.$emit('input', [...this.value, item])
    },

    onUnselect (item) {
      let value = [...this.value]
      if (this.usePrimitiveValue) {
        value = value.filter(v => v !== item)
      } else if (this.itemValue) {
        value = value.filter(v => v !== item)
      } else {
        const id = item.id
        value = value.filter(el => el.id !== id)
      }
      this.$emit('input', value)
    },

    onScrollComplete () {
      this.$emit('scroll-complete')
    },

    getItemNameByValue (item) {
      return !this.itemValue
        ? item[this.itemName]
        : this.options
          .find(el => el[this.itemValue] === item)[this.itemName]
    }
  },

  computed: {
    selectedLabel () {
      return this.value && this.value.length
        ? this.value
          .map(item => this.$t(this.getItemNameByValue(item)))
          .join(', ')
        : ''
    },

    filteredOptions () {
      return this.search
        ? this.options
          .filter(item => item[this.itemName] && item[this.itemName].toLowerCase().includes(this.search.toLowerCase()))
        : this.options
    }
  }
}
</script>

<style lang="scss">
  @import '@/sass/abstracts/_variables.scss';
  @import './_variables.scss';

  .v-multiselect-wrapper {
    padding-top: 0 !important;
    position: relative;
    margin-bottom: 0.2rem;
  }

  .v-multiselect__label {
    display: block;
    color: $color-gray !important;
    font-size: 1.3rem !important;
    pointer-events: none;
    line-height: 2rem !important;
    position: unset !important;
    margin-bottom: 2px;

    &--invalid {
      color: $color-red !important;
    }
  }

  .v-multiselect {
    position: relative;

    &__hint-wrapper {
      min-height: 1.5rem;
    }

    &__hint {
      color: $color-red;
      font-size: 1.2rem;
      font-weight: 300;
    }

    &__field {
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      height: 4rem;
      padding: 1rem;
      border-radius: 1rem;
      background: $color-hover;
      transition: background-color 0.2s ease;

      &--selected {
        background: #F4F4F4;
      }

      &--toggled {
        background: #ddf2e3;
      }

      &-input {
        width: 100%;
        border: none;
        font-size: 1.4rem;
        background: transparent;

        &:focus {
          outline: none;
        }

        &:focus ~ .v-multiselect__bar:before,
        &:focus ~ .v-multiselect__bar:after {
          width: 50%;
        }
      }
    }

    &__placeholder {
      flex: 1;
      user-select: none;
      pointer-events: none;
      font-size: 1.5rem;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      max-width: 24rem;

      &--empty {
        color: $color-gray;
        font-weight: 400;
      }
    }

    &__chevron {
      flex-shrink: 0;
      display: flex;
      align-items: center;
    }

    &__options {
      padding: 1rem 0;
      list-style: none;
      display: flex;
      flex-direction: column;
      background-color: $v-multiselect-list-bg;

      &-info,
      &-item {
        font-size: 1.5rem;
        padding: 1.2rem 1.6rem;
      }

      &-info {
        text-align: center;
      }

      &-item {
        position: relative;
        cursor: pointer;
        user-select: none;
        padding-left: 5rem;
        display: flex;
        flex-direction: column;
        border-radius: 8px;

        &--subtitle {
          color: $color-gray;
          font-size: 1.2rem;
          padding-top: .5rem;
        }

        &:hover {
          background-color: #EEEEEE;
        }

        &::before {
          content: "";
          position: absolute;
          left: 1.3rem;
          top: 50%;
          transform: translateY(-50%);
          height: 2rem;
          width: 2rem;
          border-radius: 4px;
          border: 0.15rem solid #ccc;
          background-color: #fff;
        }

        &--selected {
          background-color: #F4F4F4;

          &::after {
            content: "";
            position: absolute;
            left: 1.6rem;
            top: 50%;
            transform: translateY(-50%);
            height: 1rem;
            width: 1.4rem;
            background-image: url('~@/assets/img/check-white.svg');
            background-repeat: no-repeat;
            background-size: contain;
          }
        }
      }
    }
  }
</style>